<template>
  <NuxtLayout :name="layoutName" :navbar-color="layoutNavbarColor">
    <div v-if="story" class="blog-home">
      <div class="container">
        <StoryblokComponent
          v-for="section in storyData.story.content.header"
          :key="section._uid"
          :blok="section"
        />
        <BlogHomepageNav class="blog-home__nav" />
        <div>
          <section class="blog-home__section--short-margin">
            <BlogNewestArticle :article="posts?.allArticles.stories[0]" />
            <div class="blog-home__article-grid">
              <BlogArticlePreview
                v-for="article in storyData.story.content.featured_articles"
                :key="article.uuid"
                :article="article"
              />
            </div>
          </section>
          <section class="blog-home__section--short-margin">
            <BlogNewsletterCard />
          </section>
          <section class="blog-home__section">
            <nuxt-link
              class="blog-home__section-heading blog-home__section-heading--link"
              :to="'/blog/categories/technology'"
            >
              Technology
            </nuxt-link>
            <div class="blog-home__article-grid">
              <BlogArticlePreview
                v-for="article in storyData.story.content.technology_articles"
                :key="article.uuid"
                :article="article"
              />
            </div>
          </section>
          <section class="blog-home__section">
            <nuxt-link
              class="blog-home__section-heading blog-home__section-heading--link"
              :to="'/blog/categories/digital-health'"
            >
              Digital health
            </nuxt-link>
            <div class="blog-home__article-grid">
              <BlogArticlePreview
                v-for="article in storyData.story.content
                  .digital_health_articles"
                :key="article.uuid"
                :article="article"
              />
            </div>
          </section>
          <section class="blog-home__section">
            <nuxt-link
              class="blog-home__section-heading blog-home__section-heading--link"
              :to="'/blog/categories/company'"
            >
              Company
            </nuxt-link>
            <div class="blog-home__article-grid">
              <BlogArticlePreview
                v-for="article in storyData.story.content.company_articles"
                :key="article.uuid"
                :article="article"
              />
            </div>
          </section>
          <section class="blog-home__section">
            <nuxt-link
              class="blog-home__section-heading blog-home__section-heading--link"
              :to="'/blog/authors'"
            >
              Our authors
            </nuxt-link>
            <p class="blog-home__section-description">
              Meet the amazing group of visionaries and enthusiasts of digital
              health, who shares unique thoughts and knowledge on Infermedica
              Blog.
            </p>
            <div class="blog-home__authors-grid">
              <BlogAuthorCard
                v-for="author in storyData.story.content.authors_section"
                :key="author.uuid"
                :author="author"
                :is-description-hidden="true"
              />
            </div>
          </section>
          <section class="blog-home__section--big-margin">
            <h2 class="blog-home__section-heading">Explore all topics</h2>
            <BlogCategories :categories="posts?.categories.stories" />
          </section>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
const storyblokApi = useStoryblokApi()
const config = useRuntimeConfig()
const { story } = await useFetchStory('blog')

const queryParamsGeneral = {
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors', 'sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 4,
  filter_query: {
    component: {
      in: 'sb-blogpost',
    },
  },
}

const queryParamsCompany = {
  version: config.public.storyblokApiVersion as 'draft' | 'published',
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors', 'sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 2,
  filter_query: {
    component: {
      in: 'sb-blogpost',
    },
    categories: {
      all_in_array: 'c8c82cc5-0fbb-40b6-9980-777b56ffadc7',
    },
  },
}

const queryParamsTechnology = {
  version: config.public.storyblokApiVersion as 'draft' | 'published',
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors', 'sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 2,
  filter_query: {
    component: {
      in: 'sb-blogpost',
    },
    categories: {
      all_in_array: 'e0c5433b-dd42-4822-ab54-cef428545e2d',
    },
  },
}

const queryParamsDigitalHealth = {
  version: config.public.storyblokApiVersion as 'draft' | 'published',
  starts_with: 'blog/articles',
  resolve_relations: ['sb-blogpost.authors', 'sb-blogpost.categories'],
  sort_by: 'first_published_at:desc',
  per_page: 4,
  filter_query: {
    component: {
      in: 'sb-blogpost',
    },
    categories: {
      all_in_array: '5d0c54e6-9a18-4a0f-9b9d-881100322986',
    },
  },
}

const storyParams = {
  version: config.public.storyblokApiVersion as 'draft' | 'published',
  resolve_relations: [
    'blog-homepage.authors_section',
    'blog-homepage.topics_section',
    'blog-homepage.featured_articles',
    'blog-homepage.company_articles',
    'blog-homepage.technology_articles',
    'blog-homepage.digital_health_articles',
    'sb-blogpost.authors',
  ],
}

const { data: storyData } = await storyblokApi.get(
  'cdn/stories/blog',
  storyParams,
)

const { data: posts } = await useAsyncData('posts', async () => {
  const [
    allArticles,
    companyArticles,
    technologyArticles,
    digitalHealthArticles,
  ] = await Promise.all([
    storyblokApi.get('cdn/stories', queryParamsGeneral),
    storyblokApi.get('cdn/stories', queryParamsCompany),
    storyblokApi.get('cdn/stories', queryParamsTechnology),
    storyblokApi.get('cdn/stories', queryParamsDigitalHealth),
  ])

  const queryParamsCategories = {
    starts_with: 'blog/categories',
    filter_query: {
      component: {
        in: 'blog-category',
      },
    },
    per_page: 100,
  }

  const categories = await storyblokApi.get(
    'cdn/stories',
    queryParamsCategories,
  )

  return {
    allArticles: allArticles.data,
    companyArticles: companyArticles.data,
    technologyArticles: technologyArticles.data,
    digitalHealthArticles: digitalHealthArticles.data,
    categories: categories.data,
  }
})
false && /*#__PURE__*/ defineI18nRoute({
  locales: ['en'],
})
useCustomHead(story)

const { layoutName, layoutNavbarColor } = useLayout<any>(story)
</script>

<style lang="scss" scoped>
.blog-home {
  background-color: var(--paper-100);
  padding: 40px 0;

  @media (min-width: $breakpoint-md) {
    padding: 60px 0;
  }

  &__nav {
    margin-bottom: 40px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 64px;
    }
  }

  &__section-heading {
    margin-bottom: 32px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 40px;
    }

    &--link {
      @include heading-2;

      color: var(--cobalt-800);
      display: flex;
      align-items: center;
      width: fit-content;

      &:hover {
        color: var(--cobalt-600);
        text-decoration: none;

        &::after {
          @include icon(
            '@/assets/icons/16-chevron-right-cobalt.svg',
            16px,
            16px
          );

          content: '';
          display: block;
          margin-left: 16px;
        }
      }

      &::after {
        @include icon('@/assets/icons/16-chevron-right-navy.svg', 16px, 16px);

        content: '';
        display: block;
        margin-left: 16px;
      }
    }
  }

  &__article-grid {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 56px;

    @media (min-width: $breakpoint-md) {
      grid-template-columns: 1fr 1fr;
      column-gap: 32px;
      row-gap: 96px;
    }
  }

  &__section {
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background-color: var(--gray-400);
      margin: 56px 0;
    }

    &--short-margin {
      margin-bottom: 56px;

      @media (min-width: $breakpoint-md) {
        margin-bottom: 96px;
      }
    }

    &--big-margin {
      margin-bottom: 80px;

      @media (min-width: $breakpoint-md) {
        margin-bottom: 128px;
      }
    }

    @media (min-width: $breakpoint-md) {
      &::after {
        content: '';
        display: block;
        width: 100%;
        height: 1px;
        background-color: var(--gray-400);
        margin: 96px 0;
      }
    }
  }

  &__authors-grid {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 48px;

    @media (min-width: $breakpoint-md) {
      grid-template-columns: 1fr 1fr;
      column-gap: 74px;
      row-gap: 40px;
    }
  }

  &__section-description {
    margin-bottom: 48px;
    max-width: 488px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 80px;
    }
  }

  &__category-pill {
    padding: 8px 16px;
    background-color: white;
    color: var(--cobalt-800);
    border-radius: 12px;

    &:hover {
      color: var(--color-link);
      text-decoration: none;
    }
  }

  &__categories-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }
}
</style>
